import { FamilyMember, Post } from '../types';

export interface SearchResult {
  type: 'member' | 'post';
  id: string;
  title: string;
  subtitle?: string;
  avatar?: string;
  timestamp?: string;
  data: FamilyMember | Post;
}

/**
 * Normalize text for search (lowercase, remove accents, etc)
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics
}

/**
 * Check if a member matches search query
 */
function memberMatches(member: FamilyMember, query: string): boolean {
  const normalized = normalizeText(query);
  
  return (
    normalizeText(member.name).includes(normalized) ||
    normalizeText(member.email || '').includes(normalized) ||
    (member.address && normalizeText(member.address).includes(normalized)) ||
    (member.occupation && normalizeText(member.occupation).includes(normalized))
  );
}

/**
 * Check if a post matches search query
 */
function postMatches(post: Post, query: string): boolean {
  const normalized = normalizeText(query);
  
  return (
    normalizeText(post.content).includes(normalized) ||
    normalizeText(post.authorName || '').includes(normalized) ||
    (post.tags && post.tags.some(tag => normalizeText(tag).includes(normalized)))
  );
}

/**
 * Search members by name, email, occupation, or address
 */
export function searchMembers(
  members: Record<string, FamilyMember>,
  query: string,
  limit: number = 20
): SearchResult[] {
  if (!query.trim()) return [];
  
  return Object.values(members)
    .filter(member => memberMatches(member, query))
    .slice(0, limit)
    .map(member => ({
      type: 'member' as const,
      id: member.id,
      title: member.name,
      subtitle: member.email || member.occupation,
      avatar: member.avatar,
      data: member,
    }));
}

/**
 * Search posts by content, author, or tags
 */
export function searchPosts(
  posts: Post[],
  query: string,
  limit: number = 20
): SearchResult[] {
  if (!query.trim()) return [];
  
  return posts
    .filter(post => postMatches(post, query))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, limit)
    .map(post => ({
      type: 'post' as const,
      id: post.id,
      title: post.content.substring(0, 60) + (post.content.length > 60 ? '...' : ''),
      subtitle: post.authorName,
      timestamp: post.timestamp,
      data: post,
    }));
}

/**
 * Combined search across members and posts
 */
export function search(
  members: Record<string, FamilyMember>,
  posts: Post[],
  query: string,
  limit: number = 50
): SearchResult[] {
  const memberResults = searchMembers(members, query, Math.ceil(limit * 0.6));
  const postResults = searchPosts(posts, query, Math.ceil(limit * 0.4));
  
  // Interleave results (members first, then posts)
  const results: SearchResult[] = [];
  let m = 0, p = 0;
  
  while (results.length < limit && (m < memberResults.length || p < postResults.length)) {
    if (m < memberResults.length) {
      results.push(memberResults[m++]);
    }
    if (p < postResults.length && results.length < limit) {
      results.push(postResults[p++]);
    }
  }
  
  return results;
}

/**
 * Get search suggestions (member names for autocomplete)
 */
export function getSearchSuggestions(
  members: Record<string, FamilyMember>,
  query: string,
  limit: number = 5
): string[] {
  if (!query.trim()) return [];
  
  const normalized = normalizeText(query);
  
  return Object.values(members)
    .filter(member => normalizeText(member.name).includes(normalized))
    .slice(0, limit)
    .map(member => member.name);
}

/**
 * Get recent searches from localStorage
 */
export function getRecentSearches(): string[] {
  const recent = localStorage.getItem('recentSearches');
  return recent ? JSON.parse(recent) : [];
}

/**
 * Save search to recent searches
 */
export function saveRecentSearch(query: string): void {
  if (!query.trim()) return;
  
  let recent = getRecentSearches();
  
  // Remove duplicate if exists
  recent = recent.filter(item => item !== query);
  
  // Add to front
  recent.unshift(query);
  
  // Keep only last 10
  recent = recent.slice(0, 10);
  
  localStorage.setItem('recentSearches', JSON.stringify(recent));
}

/**
 * Clear recent searches
 */
export function clearRecentSearches(): void {
  localStorage.removeItem('recentSearches');
}
